import React from 'react';
import { useTheme } from '../context/ThemeContext';

const PrivacyPage = () => {
  const { theme } = useTheme();

  return (
    <div className={`min-h-screen pt-24 md:pt-32 pb-16 transition-colors duration-300 ${
      theme === 'dark' ? 'bg-black' : 'bg-gray-50'
    }`}>
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-12">
          <h1 className={`text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 ${
            theme === 'dark' ? 'text-white' : 'text-gray-900'
          }`}>
            Privacy Policy
          </h1>
          <p className={`text-sm ${
            theme === 'dark' ? 'text-white/50' : 'text-gray-500'
          }`}>
            Last updated: January 2025
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          {/* Intro */}
          <p className={`text-base leading-relaxed ${
            theme === 'dark' ? 'text-white/70' : 'text-gray-600'
          }`}>
            RKS Technologies ("RKS Technologies", "we", "our", or "us") operates the website{' '}
            <a 
              href="https://rkstechnologiespa.com" 
              target="_blank" 
              rel="noopener noreferrer"
              className={theme === 'dark' ? 'text-[#00FFD1] hover:underline' : 'text-[#00997A] hover:underline'}
            >
              https://rkstechnologiespa.com
            </a>{' '}
            (the "Website"). We are committed to protecting your privacy and ensuring transparency in how we collect and use information.
          </p>

          {/* Section 1 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>1. Information We Collect</h2>
            <p className={`mb-4 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We may collect the following types of information:
            </p>

            {/* 1a */}
            <h3 className={`text-lg font-semibold mb-3 ${
              theme === 'dark' ? 'text-white/90' : 'text-gray-800'
            }`}>a. Personal Information</h3>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              When you contact us, submit a form, or engage our services, you may voluntarily provide:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>Name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Company name</li>
              <li>Resume, portfolio, or professional details (for talent sourcing)</li>
            </ul>

            {/* 1b */}
            <h3 className={`text-lg font-semibold mb-3 ${
              theme === 'dark' ? 'text-white/90' : 'text-gray-800'
            }`}>b. Non-Personal Information</h3>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We may automatically collect:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device information</li>
              <li>Pages visited and usage data</li>
            </ul>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              This data is used for analytics and website improvement.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>2. How We Use Your Information</h2>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We use collected information to:
            </p>
            <ul className={`list-disc pl-6 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>Provide IT talent sourcing and consulting services</li>
              <li>Respond to inquiries and requests</li>
              <li>Communicate about services or updates</li>
              <li>Improve website performance and user experience</li>
              <li>Maintain security and prevent fraud</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>3. Sharing of Information</h2>
            <p className={`mb-3 leading-relaxed font-medium ${
              theme === 'dark' ? 'text-white/80' : 'text-gray-700'
            }`}>
              We do not sell your personal data.
            </p>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We may share information only:
            </p>
            <ul className={`list-disc pl-6 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>With trusted service providers assisting in operations</li>
              <li>When required by law or legal process</li>
              <li>To protect the rights, safety, and property of RKS Technologies</li>
            </ul>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>4. Data Security</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We implement reasonable technical and organizational measures to protect your information. However, no online transmission is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>5. Cookies</h2>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We may use cookies or similar technologies to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>Analyze traffic and usage patterns</li>
              <li>Improve functionality</li>
            </ul>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              You can disable cookies through your browser settings.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>6. Third-Party Links</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              Our Website may contain links to third-party websites. We are not responsible for their privacy practices or content.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>7. Your Rights</h2>
            <p className={`mb-3 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              Depending on your location, you may have the right to:
            </p>
            <ul className={`list-disc pl-6 mb-4 space-y-1 ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              <li>Access your personal data</li>
              <li>Request correction or deletion</li>
              <li>Withdraw consent</li>
            </ul>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              To exercise these rights, contact us at the email below.
            </p>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>8. Changes to This Policy</h2>
            <p className={`leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated date.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className={`text-xl sm:text-2xl font-semibold mb-4 ${
              theme === 'dark' ? 'text-white' : 'text-gray-900'
            }`}>9. Contact Us</h2>
            <p className={`mb-4 leading-relaxed ${
              theme === 'dark' ? 'text-white/70' : 'text-gray-600'
            }`}>
              If you have any questions about this Privacy Policy, contact us at:
            </p>
            <div className={`p-6 ${
              theme === 'dark' ? 'bg-white/5 border border-white/10' : 'bg-gray-100 border border-gray-200'
            }`}>
              <p className={`mb-2 ${
                theme === 'dark' ? 'text-white/70' : 'text-gray-600'
              }`}>
                <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Email:</strong>{' '}
                <a 
                  href="mailto:info@rkstechnologiespa.com"
                  className={theme === 'dark' ? 'text-[#00FFD1] hover:underline' : 'text-[#00997A] hover:underline'}
                >
                  info@rkstechnologiespa.com
                </a>
              </p>
              <p className={`${
                theme === 'dark' ? 'text-white/70' : 'text-gray-600'
              }`}>
                <strong className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>Website:</strong>{' '}
                <a 
                  href="https://rkstechnologiespa.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={theme === 'dark' ? 'text-[#00FFD1] hover:underline' : 'text-[#00997A] hover:underline'}
                >
                  https://rkstechnologiespa.com
                </a>
              </p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
